import { createTheme, type ThemeOptions } from '@mui/material/styles';

// DeepHealth Brand Colors (inspired by format-artist)
export const deepHealthColors = {
  // Primary Purple
  primary: {
    main: '#a10693',
    light: '#c73bb3', 
    dark: '#710667',
    contrastText: '#ffffff',
  },
  // Secondary Blue
  secondary: {
    main: '#2185f2',
    light: '#5ba3f5',
    dark: '#1969c7',
    contrastText: '#ffffff',
  },
  // Accent Colors
  accent: {
    cyan: '#abb8c3',
    purple: '#a10693',
    blue: '#2185f2',
  },
  // Neutral Colors
  neutral: {
    50: '#fafbfc',
    100: '#f4f6f8',
    200: '#dde7ee',
    300: '#cdd7e1',
    400: '#9aa5b1',
    500: '#697586',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  }
};

// Base theme configuration (using format-artist styling)
const baseTheme: ThemeOptions = {
  typography: {
    fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '3rem', // 48px
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2.625rem', // 42px - matching DeepHealth X-Large
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '2.25rem', // 36px - matching DeepHealth Large
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.875rem', // 30px
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.5rem', // 24px
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1.25rem', // 20px - matching DeepHealth Medium
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem', // 16px base
      lineHeight: 1.7,
    },
    body2: {
      fontSize: '0.8125rem', // 13px - matching DeepHealth Small
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none' as const,
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8, // DeepHealth uses less rounded corners
  },
  spacing: 6, // Reduced from 8 to make layout more compact
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
    '0 2px 4px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    '0 3px 6px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.03)',
    '0 4px 8px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)',
    '0 5px 10px rgba(0,0,0,0.1), 0 2px 5px rgba(0,0,0,0.05)',
    '6px 6px 9px rgba(0, 0, 0, 0.2)',
    '0 7px 14px rgba(0,0,0,0.13), 0 3px 6px rgba(0,0,0,0.07)',
    '0 8px 16px rgba(0,0,0,0.15), 0 3px 6px rgba(0,0,0,0.08)',
    '0 9px 18px rgba(0,0,0,0.17), 0 3px 7px rgba(0,0,0,0.09)',
    '0 10px 20px rgba(0,0,0,0.19), 0 4px 8px rgba(0,0,0,0.1)',
    '0 11px 22px rgba(0,0,0,0.21), 0 4px 9px rgba(0,0,0,0.11)',
    '12px 12px 50px rgba(0, 0, 0, 0.4)',
    '0 13px 26px rgba(0,0,0,0.25), 0 5px 10px rgba(0,0,0,0.13)',
    '0 14px 28px rgba(0,0,0,0.27), 0 5px 11px rgba(0,0,0,0.14)',
    '0 15px 30px rgba(0,0,0,0.29), 0 5px 12px rgba(0,0,0,0.15)',
    '0 16px 32px rgba(0,0,0,0.31), 0 6px 13px rgba(0,0,0,0.16)',
    '0 17px 34px rgba(0,0,0,0.33), 0 6px 14px rgba(0,0,0,0.17)',
    '0 18px 36px rgba(0,0,0,0.35), 0 6px 15px rgba(0,0,0,0.18)',
    '0 19px 38px rgba(0,0,0,0.37), 0 7px 16px rgba(0,0,0,0.19)',
    '0 20px 40px rgba(0,0,0,0.39), 0 7px 17px rgba(0,0,0,0.2)',
    '0 21px 42px rgba(0,0,0,0.41), 0 7px 18px rgba(0,0,0,0.21)',
    '0 22px 44px rgba(0,0,0,0.43), 0 8px 19px rgba(0,0,0,0.22)',
    '0 23px 46px rgba(0,0,0,0.45), 0 8px 20px rgba(0,0,0,0.23)',
    '0 24px 48px rgba(0,0,0,0.47), 0 8px 21px rgba(0,0,0,0.24)',
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#cbd5e1',
            borderRadius: '20px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          padding: '8px 16px',
          fontSize: '0.875rem',
          fontWeight: 500,
          boxShadow: 'none',
          transition: 'all 0.2s ease',
          minHeight: '32px',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          },
        },
        sizeLarge: {
          padding: '12px 24px',
          fontSize: '1rem',
          minHeight: '40px',
        },
        sizeSmall: {
          padding: '6px 12px',
          fontSize: '0.8125rem',
          minHeight: '28px',
        },
        contained: {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
          transition: 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: '6px 6px 9px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 1,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)',
        },
        elevation6: {
          boxShadow: '6px 6px 9px rgba(0, 0, 0, 0.2)',
        },
        elevation12: {
          boxShadow: '12px 12px 50px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '& fieldset': {
              borderWidth: '2px',
            },
            '&:hover fieldset': {
              borderWidth: '2px',
            },
            '&.Mui-focused fieldset': {
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          height: '24px',
        },
        sizeSmall: {
          height: '20px',
          fontSize: '0.7rem',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: '12px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
        sizeSmall: {
          fontSize: '0.8125rem',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
      },
    },
  },
};

// Light theme
export const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'light',
    primary: deepHealthColors.primary,
    secondary: deepHealthColors.secondary,
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#111827',
      secondary: '#6b7280',
    },
    divider: '#e5e7eb',
    action: {
      hover: 'rgba(161, 6, 147, 0.04)',
      selected: 'rgba(161, 6, 147, 0.08)',
    },
  },
  components: {
    ...baseTheme.components,
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#111827',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(8px)',
        },
      },
    },
  },
});

// Dark theme
export const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: 'dark',
    primary: {
      ...deepHealthColors.primary,
      main: '#c73bb3', // Lighter purple for dark mode
    },
    secondary: deepHealthColors.secondary,
    background: {
      default: '#0a0a0a',
      paper: '#1a1a1a',
    },
    text: {
      primary: '#ffffff',
      secondary: deepHealthColors.accent.cyan,
    },
    divider: '#374151',
    action: {
      hover: 'rgba(199, 59, 179, 0.08)',
      selected: 'rgba(199, 59, 179, 0.12)',
    },
  },
  components: {
    ...baseTheme.components,
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(8px)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          borderRadius: 8,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.4)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          backgroundImage: 'none',
        },
      },
    },
  },
});

// Custom shadow system (DeepHealth style)
export const deepHealthShadows = {
  natural: '6px 6px 9px rgba(0, 0, 0, 0.2)',
  deep: '12px 12px 50px rgba(0, 0, 0, 0.4)',
  glass: '0 8px 32px rgba(0, 0, 0, 0.06)',
  material: [
    'none',
    '0 2px 10px rgba(0, 0, 0, 0.05)',
    '0 4px 20px rgba(0, 0, 0, 0.1)',
    '0 8px 30px rgba(0, 0, 0, 0.15)',
    '0 16px 40px rgba(0, 0, 0, 0.2)',
  ],
};